// Ordering, filtering and the small judgements the all-time tables would
// otherwise make inline.
//
// Outside the components for the reason offersSort.ts already records: vitest
// here has no React renderer, so anything left inside a component cannot be
// tested at all — and which row comes first is exactly the kind of thing that
// is wrong quietly.
import type { MarketItem, MarketLot, MarketQuote, OfferMode } from "../api/types";
import { bestFirst, byPrice, byText } from "./compare";

export interface ModeRow {
  item_id: number;
  item: string;
  quote: MarketQuote;
}

export type ItemSort = "seen" | "item" | "buy" | "sell" | "spread";
export type ModeSort = "seen" | "item" | "best" | "traders";
export type MarketLotSort = "seen" | "trader" | "total";

/** Older than this and the date is dimmed. A week is about the point at which a
 *  price stops being something you would act on without re-checking. It lives
 *  here rather than in a component so it can be tested. */
export const STALE_AFTER_DAYS = 7;

const DAY = 86_400;
const NOTHING = "—";

function best(item: MarketItem, mode: OfferMode): number | null {
  return item[mode]?.best ?? null;
}

export function sortMarketItems(items: MarketItem[], sort: ItemSort): MarketItem[] {
  // A copy: Array.prototype.sort is in place and these rows come straight out of
  // react-query's cache. Sort is stable in ES2019+, which is what keeps equal
  // rows from swapping between renders.
  return [...items].sort((a, b) => {
    if (sort === "item") return byText(a.item, b.item);
    if (sort === "buy") return byPrice(best(a, "buy"), best(b, "buy"), bestFirst("buy"))
      || byText(a.item, b.item);
    if (sort === "sell") return byPrice(best(a, "sell"), best(b, "sell"), bestFirst("sell"))
      || byText(a.item, b.item);
    // Widest first: the interesting end of a spread is the big one.
    if (sort === "spread") return byPrice(spread(a), spread(b), -1) || byText(a.item, b.item);
    return b.last_seen - a.last_seen || byText(a.item, b.item);
  });
}

/** One mode's page, from the shared response. An item with no quote for that
 *  mode is not on that page — it was never offered that way. */
export function modeRows(items: MarketItem[], mode: OfferMode): ModeRow[] {
  const rows: ModeRow[] = [];
  for (const item of items) {
    const quote = item[mode];
    if (quote) rows.push({ item_id: item.item_id, item: item.item, quote });
  }
  return rows;
}

export function sortModeRows(rows: ModeRow[], sort: ModeSort, mode: OfferMode): ModeRow[] {
  const direction = bestFirst(mode);
  return [...rows].sort((a, b) => {
    if (sort === "item") return byText(a.item, b.item);
    if (sort === "best") return byPrice(a.quote.best, b.quote.best, direction)
      || byText(a.item, b.item);
    // Busiest first: a count answers "how many", and the interesting end of
    // that question is the big one.
    if (sort === "traders") return b.quote.traders - a.quote.traders || byText(a.item, b.item);
    return b.quote.observed_at - a.quote.observed_at || byText(a.item, b.item);
  });
}

/** Identity, not a copy, on an empty filter — so a page that filters on every
 *  render does not hand react a new array each time for no reason. */
export function filterByItem<T extends { item: string }>(rows: T[], needle: string): T[] {
  const term = needle.trim().toLowerCase();
  if (!term) return rows;
  return rows.filter((row) => row.item.toLowerCase().includes(term));
}

export function sortMarketLots(lots: MarketLot[], sort: MarketLotSort): MarketLot[] {
  return [...lots].sort((a, b) => {
    if (sort === "trader") return byText(a.nick, b.nick);
    if (sort === "total") return byPrice(a.total, b.total, -1) || byText(a.nick, b.nick);
    return b.last_seen - a.last_seen || byText(a.nick, b.nick);
  });
}

export function filterMarketLots(lots: MarketLot[], needle: string): MarketLot[] {
  const term = needle.trim().toLowerCase();
  if (!term) return lots;
  return lots.filter((lot) =>
    lot.items.some((item) => item.name.toLowerCase().includes(term)));
}

export function isStale(epochSeconds: number, now: number = Date.now() / 1000): boolean {
  return now - epochSeconds > STALE_AFTER_DAYS * DAY;
}

/** The move against the previous scan that saw this item.
 *
 *  Null unless the current best and the previous one were both readable. A
 *  failed read is not a price, and subtracting from one would print a number
 *  that looks like a move and means nothing. */
export function delta(quote: MarketQuote | null): number | null {
  if (!quote || quote.best === null || quote.previous === null) return null;
  return quote.best - quote.previous;
}

/** Buy minus sell, positive when someone pays more than someone asks — the
 *  client's meaning, not re-invented. Null unless both sides are known. */
export function spread(item: MarketItem): number | null {
  const buy = best(item, "buy");
  const sell = best(item, "sell");
  return buy === null || sell === null ? null : buy - sell;
}

/** Did the two sides come from different scans? A spread between a two-hour-old
 *  bid and a three-week-old ask is arithmetic, not a market — so the table dims
 *  it and says why. Blanking it instead would empty the column for most rows. */
export function crossScanSpread(item: MarketItem): boolean {
  if (!item.buy || !item.sell) return false;
  return item.buy.scan_uid !== item.sell.scan_uid;
}

/** Which town to print, and whether it needs explaining. The newer side wins,
 *  because that is the number the eye goes to. A tooltip only when the sides
 *  disagree: one that repeats the cell is noise. */
export function whereCity(item: MarketItem): { city: string; title?: string } {
  const quotes = [item.buy, item.sell, item.craft]
    .filter((quote): quote is MarketQuote => quote !== null && quote.city !== "")
    .sort((a, b) => b.observed_at - a.observed_at);
  if (quotes.length === 0) return { city: NOTHING };

  const towns = [...new Set(quotes.map((quote) => quote.city))];
  return towns.length === 1
    ? { city: towns[0] }
    : { city: towns[0], title: `these figures came from different towns: ${towns.join(", ")}` };
}
